import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Volume2, VolumeX, MousePointerClick, CreditCard, CalendarCheck, Mail, QrCode } from "lucide-react";
import { useLang } from "@/i18n";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: 1, icon: MousePointerClick },
  { key: 2, icon: CreditCard },
  { key: 3, icon: CalendarCheck },
  { key: 4, icon: Mail },
];

const DEFAULTS = {
  video_url: "/assets/video-inscription.mp4",
  steps_images: ["/assets/etape-1-formulaire.jpg", "/assets/etape-2-paiement.jpg", "/assets/etape-3-rendezvous.jpg", "/assets/etape-4-equipe.jpg"],
};

export default function CommentInscrirePage() {
  const { t } = useLang();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [howto, setHowto] = useState(DEFAULTS);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/howto`).then((r) => setHowto(r.data)).catch(() => {});
  }, []);

  const toggleSound = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      if (videoRef.current.paused) videoRef.current.play().catch(() => {});
      setMuted(!muted);
    }
  };

  return (
    <div data-testid="howto-page" className="grain bg-[#0A0A0C] text-white min-h-screen">
      <header className="border-b border-[#27272A] bg-[#0A0A0C]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="howto-back" className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#C5A059] transition-colors text-xs uppercase tracking-[0.2em]">
            <ArrowLeft size={15} /> {t("eventpage.back")}
          </Link>
          <img src="/assets/logo-dark-sm.png" alt="DREAM2REALITY Football Agency" className="h-10 w-auto" />
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-8 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("howto.eyebrow")}</p>
            <h1 className="font-display text-5xl lg:text-7xl uppercase font-bold leading-[0.95]">
              {t("howto.title.a")} <span className="text-[#C5A059]">{t("howto.title.b")}</span>
            </h1>
            <p className="text-base text-[#A1A1AA] mt-6 max-w-2xl mx-auto leading-relaxed">{t("howto.text")}</p>
          </motion.div>
        </section>

        {/* Téléphone central avec la vidéo (masqué si l'admin a retiré la vidéo) */}
        {howto.video_url && (
        <section className="flex flex-col items-center px-6 py-12" data-testid="howto-video-section">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Cadre du téléphone */}
            <div className="relative w-[300px] sm:w-[340px] rounded-[3rem] border-[10px] border-[#27272A] bg-black shadow-[0_0_80px_rgba(197,160,89,0.15)] overflow-hidden">
              {/* Encoche */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#27272A] rounded-b-2xl z-10" />
              <video
                key={howto.video_url}
                ref={videoRef}
                src={howto.video_url}
                poster="/assets/affiche-kinshasa.png"
                className="w-full aspect-[720/1280] object-cover"
                autoPlay
                loop
                muted
                playsInline
                controls
                data-testid="howto-video"
              />
            </div>
            <button
              onClick={toggleSound}
              data-testid="howto-sound-toggle"
              className="mt-6 mx-auto flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-6 py-3 text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-black transition-colors duration-300"
            >
              {muted ? <Volume2 size={14} /> : <VolumeX size={14} />}
              {muted ? t("howto.sound.on") : t("howto.sound.off")}
            </button>
          </motion.div>
        </section>
        )}

        {/* Étapes détaillées */}
        <section className="max-w-5xl mx-auto px-6 lg:px-10 py-16 space-y-20" data-testid="howto-steps">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className={`flex flex-col ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} items-center gap-10`}
              data-testid={`howto-step-${step.key}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-display text-5xl font-bold text-[#C5A059]/30">0{step.key}</span>
                  <step.icon size={22} className="text-[#C5A059]" />
                </div>
                <h3 className="font-display text-2xl uppercase font-bold">{t(`howto.step${step.key}.t`)}</h3>
                <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">{t(`howto.step${step.key}.x`)}</p>
              </div>
              <div className="flex-1 w-full">
                <img src={howto.steps_images[i]} alt={t(`howto.step${step.key}.t`)} loading="lazy"
                  className="w-full border border-[#27272A] shadow-2xl hover:border-[#C5A059]/50 transition-colors duration-300" />
              </div>
            </motion.div>
          ))}
        </section>

        {/* QR code + lien */}
        <section className="border-t border-[#27272A] bg-[#0D0D10]">
          <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="howto-qr-section">
            <QrCode size={24} className="text-[#C5A059] mx-auto mb-6" />
            <h2 className="font-display text-3xl uppercase font-bold">{t("howto.qr.title")}</h2>
            <p className="text-sm text-[#A1A1AA] mt-3 mb-10">{t("howto.qr.text")}</p>
            <a href="https://dream2realityfa.com/detection" className="inline-block group">
              <img src="/assets/qr-inscription.png" alt="QR code — formulaire d'inscription D2R" data-testid="howto-qr-image"
                className="w-48 h-48 mx-auto border border-[#27272A] group-hover:border-[#C5A059] transition-colors duration-300" />
            </a>
            <a href="https://dream2realityfa.com/detection" data-testid="howto-site-link"
              className="block mt-6 text-[#C5A059] text-sm tracking-wide hover:text-[#E5C07B] transition-colors">
              dream2realityfa.com/detection
            </a>
            <Link to="/detection" data-testid="howto-cta"
              className="mt-10 inline-flex items-center gap-3 bg-[#C5A059] text-black px-10 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300">
              {t("howto.cta")}
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
